import { useTranslations } from 'use-intl';
import type { TrendWeek } from '@/lib/loadTrend';

export type { TrendWeek };

/**
 * Where the load has been going, a week at a time.
 *
 * The week profile answers "is this week shaped right". This answers the
 * question a coach cannot see from inside one week: whether the last two months
 * have been a ramp, a plateau or a drift. Same two series and the same language
 * as the profile — planned is an outline because it is an intention, actual is
 * solid because it happened — so the two pictures read as one idea at two scales.
 *
 * Hand-drawn for the same reason: eight bars and two series do not need a
 * charting library, and these keep tabular figures and RTL mirroring.
 *
 * It used to carry `min-w-[30rem]`, which gave the labels room and, on a 390px
 * phone, pushed the whole document 97px wider than the screen — a sideways
 * scroll on the first screen a coach opens. A `min-width` inside an
 * `overflow-x-auto` box still reaches the page: the box clips it, the layout
 * viewport grows anyway, and every fixed element then stretches with it.
 *
 * So the columns divide whatever width there is and the label wraps instead of
 * demanding its own line. Eight weeks stays eight weeks — that is two
 * mesocycles and the reason the number was chosen. The scroller is kept for a
 * series long enough to genuinely need one.
 */
export function LoadTrend({ weeks }: { weeks: readonly TrendWeek[] }) {
  const t = useTranslations();
  const peak = Math.max(1, ...weeks.map((w) => Math.max(w.plannedLoad, w.actualLoad)));
  const height = (load: number) => (load <= 0 ? 0 : Math.max(3, Math.round((load / peak) * 100)));

  if (weeks.length === 0) {
    return <p className="px-4 py-6 text-meta text-ink-muted">{t('dashboard.noTrend')}</p>;
  }

  return (
    <div className="overflow-x-auto px-4 py-4">
      <div className={`flex items-end gap-1 sm:gap-2 ${weeks.length > 10 ? 'min-w-[30rem]' : ''}`}>
        {weeks.map((w) => (
          <div key={w.startsOn} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
            <div className="flex h-28 w-full items-end justify-center gap-1">
              {/* The same rule: an empty week draws nothing, not a dashed line
                  on the baseline. */}
              {height(w.plannedLoad) > 0 ? (
                <div
                  className="w-3 rounded-t-sm border border-dashed border-line-strong"
                  style={{ height: `${height(w.plannedLoad)}%` }}
                  aria-hidden
                />
              ) : null}
              {height(w.actualLoad) > 0 ? (
                <div
                  className={`w-3 rounded-t-sm ${w.isCurrent ? 'bg-signal' : 'bg-ink'}`}
                  style={{ height: `${height(w.actualLoad)}%` }}
                  aria-hidden
                />
              ) : null}
            </div>
            {/* Wraps rather than truncates: "31 a…" is not a date, and Arabic
                month names are longer than the English ones this was sized for. */}
            <span
              className={`w-full text-center text-[0.625rem] leading-tight sm:text-meta ${
                w.isCurrent ? 'font-semibold text-ink' : 'text-ink-faint'
              }`}
            >
              {w.label}
            </span>
            {/* A week with a match in it is a different kind of week, and the
                shape above only makes sense once you know which ones those are. */}
            <span className="text-meta text-ink-faint" aria-hidden>
              {w.matches > 0 ? '•'.repeat(Math.min(w.matches, 3)) : ' '}
            </span>
            <span className="sr-only">
              {t('dashboard.weekReads', {
                week: w.label,
                planned: Math.round(w.plannedLoad),
                actual: Math.round(w.actualLoad),
                matches: w.matches,
              })}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-meta text-ink-muted">{t('dashboard.trendLegend')}</p>
    </div>
  );
}
